// Global
import React, { useState, useRef, useCallback } from 'react';
import { Themes } from 'lib/themes';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Preamble, PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';

// Local
import styles from './VerticalVideo.module.scss';
import { VerticalVideoGallery } from './VerticalVideoGallery';
import { VerticalVideoModal } from './VerticalVideoModal';
import type { VerticalVideoCardData } from './VerticalVideoCard/VerticalVideoCard';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import { CtaFields, HeadlineFields } from 'lib/types/headlineFields';
import clsx from 'clsx';

export interface VerticalVideoProps extends HeadlineFields, CtaFields {
  ariaLabel?: string;
  contentListName?: string;
  theme?: Themes;
  preambleOrientation?: PreambleOrientation;
  videoListItems: Array<{
    fields?: {
      videoId?: string;
      videoHeadline?: string;
      videoDescription?: string;
      videoThumbnail?: string;
      videoWithAudioDescriptionURL?: string;
      videoURL?: string;
      videoType?: string;
      videoTranscriptFileUrl?: string;
    };
  }>;
}

export const VerticalVideoMain = (props: VerticalVideoProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VerticalVideoCardData | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const { currentScreenWidth } = useCurrentScreenType();

  // Transform Sitecore items to VerticalVideoCardData
  const videoCards: VerticalVideoCardData[] = (props.videoListItems || []).reduce<
    VerticalVideoCardData[]
  >((acc, videoItem) => {
    const videoId = (videoItem.fields?.videoId ?? '').trim();
    const videoUrl = (videoItem.fields?.videoURL ?? '').trim();

    if (!videoId && !videoUrl) return acc;

    acc.push({
      videoId,
      videoTitle: videoItem.fields?.videoHeadline ?? '',
      videoDescription: videoItem.fields?.videoDescription,
      videoThumbnailUrl: videoItem.fields?.videoThumbnail ?? '',
      videoType: videoItem.fields?.videoType ?? '',
      videoUrl,
      videoWithAudioDescriptionURL: videoItem.fields?.videoWithAudioDescriptionURL,
      videoTranscriptFileUrl: videoItem.fields?.videoTranscriptFileUrl,
    });

    return acc;
  }, []);

  const handlePlayVideo = useCallback(
    (video: VerticalVideoCardData, trigger?: HTMLButtonElement) => {
      if (trigger) {
        triggerRef.current = trigger;
      }
      setSelectedVideo(video);
      setIsModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  }, []);

  if (videoCards.length === 0) {
    return <></>;
  }

  const isMobileOrTablet = currentScreenWidth !== 0 && currentScreenWidth <= 1199;
  const preambleAlignment: PreambleOrientation = isMobileOrTablet ? 'TopLeft' : 'SideLeft';

  return (
    <BrandAndThemeProvider
      theme={props.theme}
      className={clsx(styles.verticalVideoWrapper)}
      data-component="authorable/shared/content/vertical-video/vertical-video"
    >
      <section
        className={styles.verticalVideoContainer}
        aria-label={props.ariaLabel || 'Vertical Videos'}
      >
        <Preamble
          headlineEyebrow={props.headlineEyebrow}
          headlineIcon={props.headlineIcon}
          headline={props.headline}
          headlineSubhead={props.headlineSubhead}
          headlineDescription={props.headlineDescription}
          cta1={props.cta1}
          cta2={props.cta2}
          align={preambleAlignment}
        >
          <VerticalVideoGallery
            videos={videoCards}
            onPlayVideo={handlePlayVideo}
            ctaLinksLeftAligned={true}
            contentListName={props.contentListName}
          />
        </Preamble>

        <VerticalVideoModal
          isOpen={isModalOpen}
          video={selectedVideo}
          onClose={handleCloseModal}
          triggerRef={triggerRef}
        />
      </section>
    </BrandAndThemeProvider>
  );
};
